import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Button } from 'planoplan-ui-kit/core/button';
import { deviceDetected } from 'planoplan-ui-kit/core/device-detected';
import { useMediaQuery } from 'planoplan-ui-kit/core/hooks/useMediaQuery';
import { Points } from '@styles';
import { useTranslations } from '@libs/translations';
import { DownloadButton } from './organism/download-button';

export const FirstScreen = () => {
  const isTabletPortrait = useMediaQuery(Points.tablet_portrait, { useSSR: true });
  const translations = useTranslations();
  const isDesktopWide = useMediaQuery(Points.laptop_wide, { useSSR: true });
  const isMobileDevice = deviceDetected.isMobile;
  const { locale } = useSelector((state) => state.settings);

  return (
    <View id="first-screen">
      <Container>
        <Content>
          <TitleWrapper>
            <SeoTitle>{translations.t('site.home.firstscreen.heading')}</SeoTitle>
            <Title>{translations.t('site.home.firstscreen.titleUp')}</Title>
            <Title isOpacity={true}>{translations.t('site.home.firstscreen.titleDown')}</Title>
          </TitleWrapper>
          <Description dangerouslySetInnerHTML={{ __html: translations.t('site.home.firstscreen.description') }} />
          <ButtonWrapper>
            {isMobileDevice ? (
              <Wrapper>
                {locale === 'ru' && (
                  <Button
                    theme="primary"
                    data-gtm="button_click"
                    size={isDesktopWide ? 'L' : isTabletPortrait ? 'M' : 'S'}
                    href={`/${locale}/webinars`}>
                    {translations.t('site.home.firstscreen.mobileBtnWebinar')}
                  </Button>
                )}
                <Button
                  theme={locale === 'ru' ? 'secondary' : 'primary'}
                  data-gtm="button_click"
                  size={isDesktopWide ? 'L' : isTabletPortrait ? 'M' : 'S'}
                  href={`/${locale}/download`}>
                  {translations.t('site.home.firstscreen.mobileBtnDownload')}
                </Button>
              </Wrapper>
            ) : (
              <DownloadButton />
            )}
            {!isMobileDevice && <Caption>{translations.t('site.home.firstscreen.buttonCaption')}</Caption>}
          </ButtonWrapper>
        </Content>
        <Label dangerouslySetInnerHTML={{ __html: translations.t('site.firstscreen.label') }} />
      </Container>
    </View>
  );
};

const View = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: end;
  height: 100vh;
  height: 100svh;
  padding-top: 60px;
  background-image: image-set(
    url('/images/first-screen-bg-mob.webp') type('image/webp'),
    url('/images/first-screen-bg-mob.jpg') type('image/jpeg')
  );
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #454545;
  color: ${Colors.white};

  @media ${Points.tablet_portrait} {
    background-image: image-set(
      url('/images/first-screen-bg-tab.webp') type('image/webp'),
      url('/images/first-screen-bg-tab.jpg') type('image/jpeg')
    );
  }

  @media ${Points.tablet_landscape} {
    height: min(60vw, 100vh);
    height: min(60vw, 100svh);
    padding-top: 70px;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
      linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(102, 102, 102, 0.2) 74.31%),
      image-set(
        url('/images/first-screen-bg-x1.webp') 1x type('image/webp'),
        url('/images/first-screen-bg-x1.jpg') 1x type('image/jpeg'),
        url('/images/first-screen-bg-x2.webp') 2x type('image/webp'),
        url('/images/first-screen-bg-x2.jpg') 2x type('image/jpeg')
      );
    background-size: 108% auto;
    background-position: center;
  }

  @media ${Points.laptop_wide} {
    height: 100vh;
    height: 100svh;
    min-height: 500px;
    padding-top: 100px;
    background-size: cover;
  }

  @media ${Points.fullhd} {
    padding-top: 90px;
  }
`;

const Container = styled.div`
  margin: auto 16px min(30px, 4.5vh);

  @media ${Points.tablet_landscape} {
    position: relative;
    width: 100%;
    max-width: 1024px;
    margin: auto;
    margin-bottom: min(34px, 5.5vh);
    padding: 28px;
  }

  @media ${Points.laptop_wide} {
    width: 100%;
    max-width: 1600px;
    margin: auto;
    margin-left: auto;
    margin-bottom: min(100px, 10.4vh);
    padding-left: 80px;
  }

  @media ${Points.fullhd} {
    margin-left: auto;
    padding-left: 0;
  }
`;

const Label = styled.div`
  display: none;

  @media ${Points.tablet_landscape} {
    position: absolute;
    display: block;
    bottom: 0;
    right: 28px;
    opacity: 0.5;
    color: ${Colors.white};
    font-weight: 400;
    font-size: 12px;
    line-height: 1.1;
    letter-spacing: 1%;
    text-align: right;
  }

  @media ${Points.laptop_wide} {
    bottom: 0;
    right: 80px;
    font-size: 14px;
  }

  @media ${Points.fullhd} {
    right: 80px;
  }
`;

const Content = styled.div`
  @media ${Points.tablet_landscape} {
    margin-right: 22%;
  }

  @media ${Points.fullhd} {
    margin: 0;
  }
`;

const TitleWrapper = styled.div`
  margin-bottom: min(40px, 6vh);

  @media ${Points.tablet_landscape} {
    margin-bottom: min(40px, 6.5vh);
  }

  @media ${Points.laptop_wide} {
    width: 770px;
    margin-bottom: min(40px, 4.2vh);
  }
`;

const SeoTitle = styled.h1`
  margin-bottom: min(15px, 2.2vh);
  align: left;
  font-weight: 400;
  font-size: 10px;
  line-height: 1.1;
  letter-spacing: 7%;
  text-transform: uppercase;
  color: ${Colors.white};
  opacity: 0.5;

  @media ${Points.tablet_landscape} {
    font-size: 12px;
  }

  @media ${Points.laptop_wide} {
    font-size: 14px;
    margin-bottom: min(24px, 2.5vh);
  }
`;

const Title = styled.p`
  font-weight: 400;
  font-size: clamp(28px, 5.4vh, 36px);
  line-height: 1;
  letter-spacing: 0%;
  color: ${Colors.white};
  opacity: ${(p) => (p.isOpacity ? 0.6 : 1)};

  @media ${Points.tablet_landscape} {
    font-size: clamp(40px, 10.4vh, 64px);
    line-height: 0.94;
  }

  @media ${Points.laptop_wide} {
    font-size: clamp(44px, 8.5vh, 82px);
    line-height: 0.98;
    width: 500px;
  }
`;

const Description = styled.div`
  margin-right: 44px;
  margin-bottom: min(20px, 3vh);
  font-weight: 400;
  font-size: clamp(13px, 2.4vh, 16px);
  line-height: 1.3;
  letter-spacing: 0%;
  font-variant-numeric-fraction: stacked-fractions;
  color: ${Colors.white};

  @media ${Points.tablet_portrait} {
    width: 430px;
  }

  @media ${Points.tablet_landscape} {
    width: auto;
    margin-right: 343px;
    font-size: clamp(13px, 2.6vh, 16px);
  }

  @media ${Points.laptop_wide} {
    width: 430px;
    margin-bottom: min(30px, 3.1vh);
    font-size: clamp(14px, 1.9vh, 18px);
    line-height: 1.3;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;

  @media ${Points.tablet_portrait} {
    width: 280px;
  }

  @media ${Points.tablet_landscape} {
    margin-right: auto;
    width: auto;

    & button {
      max-width: 480px;
    }
  }

  @media ${Points.laptop_wide} {
    width: 770px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  & a {
    border: none;
  }

  @media ${Points.tablet_portrait} {
    width: 280px;
  }
`;

const Caption = styled.p`
  margin-top: min(15px, 2.2vh);
  font-weight: 400;
  font-size: 12px;
  line-height: 1.1;
  letter-spacing: 1%;
  color: ${Colors.white};

  @media ${Points.tablet_landscape} {
    font-size: 14px;
  }
`;
